import React, { useState, useEffect, useCallback } from "react";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import localeText from "../../../../../locale";
import BankAccountService from "../../../../../service/BankAccountService";
import { useToastContext } from "../../../../../contexts/ToastContext/ToastContext";
import BankAccountList from "./BankAccountList";
import BankAccountFormModal from "./BankAccountFormModal";

const bankAccountService = new BankAccountService();

const BankAccountsTab = ({ onAccountsChange }) => {
  const { showErrorToast, showSuccessToast } = useToastContext();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bankAccountService.list();
      if (res?.result && Array.isArray(res.result)) {
        setAccounts(res.result);
        onAccountsChange?.(res.result);
      } else {
        setAccounts([]);
        onAccountsChange?.([]);
      }
    } catch (e) {
      setAccounts([]);
      onAccountsChange?.([]);
      showErrorToast({ code: e?.response?.data?.messageCode || 500000 });
    } finally {
      setLoading(false);
    }
  }, [onAccountsChange, showErrorToast]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleAdd = () => {
    setEditingAccount(null);
    setModalVisible(true);
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setModalVisible(true);
  };

  const handleDelete = (account) => {
    confirmDialog({
      message: localeText("confirmDeleteAccount"),
      header: localeText("attention"),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: localeText("yes"),
      rejectLabel: localeText("no"),
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          const res = await bankAccountService.delete(account);
          if (res.messageCode !== 200000 && res.messageCode !== 200001) {
            showErrorToast({ code: res.messageCode });
            return;
          }
          showSuccessToast({ detail: "accountDeletedSuccess" });
          fetchList();
        } catch (e) {
          showErrorToast({ code: e?.response?.data?.messageCode || 500000 });
        }
      },
    });
  };

  const handleModalSuccess = () => {
    fetchList();
  };

  if (loading) {
    return (
      <div className="flex justify-content-center align-items-center p-6">
        <i className="pi pi-spin pi-spinner text-4xl text-500" />
      </div>
    );
  }

  return (
    <>
      <ConfirmDialog />
      <div className="flex flex-column gap-4">
        <div className="flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <h3 className="m-0 font-semibold" style={{ color: "var(--theme-text, #171717)" }}>
              {localeText("bankAccounts")}
            </h3>
            <p className="text-sm m-0 mt-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
              {localeText("financialHubSubtitle")}
            </p>
          </div>
          <Button
            label={
              <>
                <i className="pi pi-plus text-xs font-bold mr-2" /> {localeText("addAccount")}
              </>
            }
            className="border-round-lg border-none p-no-outline font-medium"
            style={{
              backgroundColor: "var(--theme-primary)",
              color: "var(--theme-background, #FFFFFF)",
              height: "40px",
            }}
            onClick={handleAdd}
          />
        </div>
        {accounts.length === 0 ? (
          <div
            className="card border-round-lg p-6 flex flex-column align-items-center justify-content-center text-center"
            style={{ border: "1px solid var(--theme-border, #EBEBEB)", minHeight: "280px" }}
          >
            <div
              className="border-circle p-4 mb-3 flex align-items-center justify-content-center"
              style={{ backgroundColor: "var(--theme-background, #FFFFFF)" }}
            >
              <i className="pi pi-wallet text-4xl text-500" />
            </div>
            <p className="m-0 mb-4 text-600" style={{ maxWidth: "400px" }}>
              {localeText("noAccountsMessage")}
            </p>
            <Button
              label={localeText("addAccount")}
              icon="pi pi-plus"
              className="border-round-lg p-button-text font-medium p-no-outline"
              style={{
                border: "1px solid var(--theme-border, #EBEBEB)",
                color: "var(--theme-text-muted, #5C5C5C)",
              }}
              onClick={handleAdd}
            />
          </div>
        ) : (
          <BankAccountList accounts={accounts} onEdit={handleEdit} onDelete={handleDelete} />
        )}
      </div>
      {modalVisible && (
        <BankAccountFormModal
          visible
          onHide={() => {
            setModalVisible(false);
            setEditingAccount(null);
          }}
          editingAccount={editingAccount}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
};

export default BankAccountsTab;
